import { Link, usePage } from '@inertiajs/react'
import Logo from '../../components/Logo'
import { useLocale } from '../../lib/LocaleContext'

const OWNER_ITEM_DEFS = [
  { key: 'workspaces', labelKey: 'settings.nav.workspaces', href: '/settings/workspaces' },
  { key: 'users',      labelKey: 'settings.nav.users',      href: '/settings/users' },
  { key: 'projects',   labelKey: 'settings.nav.projects',   href: '/settings/projects' },
  { key: 'api_keys',   labelKey: 'settings.nav.api_keys',   href: '/settings/api_keys' },
  { key: 'llm_usages', labelKey: 'settings.nav.llm_usages', href: '/settings/llm_usages' },
]

const MEMBER_ITEM_DEFS = [
  { key: 'profile',  labelKey: 'settings.nav.profile',  href: '/settings/profile/edit' },
  { key: 'password', labelKey: 'settings.nav.password', href: '/settings/password/edit' },
]

function initials(user) {
  const name = user?.display_name || user?.email || '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function UserAvatar({ user }) {
  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt="avatar"
        className="w-7 h-7 rounded-full object-cover border border-zinc-600 shrink-0"
      />
    )
  }
  return (
    <div className="w-7 h-7 rounded-full bg-zinc-700 border border-zinc-600 flex items-center justify-center text-[10px] font-mono font-bold text-zinc-300 shrink-0">
      {initials(user)}
    </div>
  )
}

export default function Layout({ children, active }) {
  const { auth, current_workspace } = usePage().props
  const user = auth?.user
  const isOwner = user?.role === 'owner'
  const { t } = useLocale()
  const navItemDefs = isOwner ? [...OWNER_ITEM_DEFS, ...MEMBER_ITEM_DEFS] : MEMBER_ITEM_DEFS
  const navItems = navItemDefs.map(item => ({ ...item, label: t(item.labelKey) }))

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100 flex flex-col">
      <header className="border-b border-zinc-700 px-6 py-3 flex items-center gap-4 shrink-0 bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-800/30">
        <Link href="/" className="flex items-center gap-2.5 no-underline group">
          <Logo variant="mark" size={28} />
          <span className="text-base font-mono font-bold tracking-[0.12em] uppercase text-zinc-100 group-hover:text-emerald-300 transition-colors">
            anamnesis
          </span>
        </Link>
        <span className="text-zinc-700">·</span>
        <span className="text-xs font-mono font-semibold text-zinc-500 uppercase tracking-widest">{t('settings.title')}</span>
        <div className="ml-auto flex items-center gap-3">
          {current_workspace?.name && (
            <span className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-xs font-mono text-zinc-400">
              {current_workspace.name}
            </span>
          )}
          <UserAvatar user={user} />
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <nav className="w-48 shrink-0 border-r border-zinc-700 px-3 py-4 space-y-0.5">
          {navItems.map(item => (
            <Link
              key={item.key}
              href={item.href}
              className={[
                'block px-2 py-1.5 rounded text-sm font-mono transition-colors border-l-2 pl-[6px]',
                active === item.key
                  ? 'bg-emerald-900/30 text-emerald-300 border-emerald-500/70'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border-transparent',
              ].join(' ')}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
